import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Container, CssBaseline, Tab, Tabs, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { injectIntl } from 'react-intl';
import InsightsIcon from '@mui/icons-material/Insights';
import TimelineIcon from '@mui/icons-material/Timeline';
import GppBadIcon from '@mui/icons-material/GppBad';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import WebIcon from '@mui/icons-material/Web';
import BugReportIcon from '@mui/icons-material/BugReport';
import LanIcon from '@mui/icons-material/Lan';
import ApiIcon from '@mui/icons-material/Api';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Styles } from '../../styles/material-styles';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import Api from '../../services/api';
import * as componentActions from '../../redux/actions/header-menu';
import UsageDashboard from './dashboard';
import UsageUserActivity from './user-activity';
import UsageBlocked from './blocked';
import UsageEntityAccess from './entity-access';
import UsageScreens from './screens';
import UsageEndpoints from './endpoints';
import UsageErrors from './errors';
import UsageIpTraffic from './ip-traffic';
import UsageSessions from './sessions';

// ---------------------------------------------------------------------------
// ÁREA "Uso do sistema": um item só no menu do usuário, quatro visões com rota própria:
//   /:lang/usage/dashboard        — dashboard (rollup)
//   /:lang/usage/user-activity    — linha do tempo do usuário (fato)
//   /:lang/usage/sessions         — logins e sessões: quem entrou, de onde, rede nova, chaves de integração
//   /:lang/usage/blocked          — recusas e bloqueios (fato + rollup)
//   /:lang/usage/ip-traffic       — tráfego por IP × endpoint (resumo por IP)
//   /:lang/usage/endpoints        — endpoints por volume; quem chamou / de onde com cadência (humano × robô)
//   /:lang/usage/errors          — erros 5xx/4xx por rota, com a exceção (fato + rollup)
//   /:lang/usage/entity-access    — quem acessou a entidade (fato)
// Só Global. As visões se cruzam por link (ranking → linha do tempo, chamado → quem acessou).
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: <InsightsIcon fontSize="small" /> },
  { id: 'screens', label: 'Telas', icon: <WebIcon fontSize="small" /> },
  { id: 'endpoints', label: 'Endpoints', icon: <ApiIcon fontSize="small" /> },
  { id: 'user-activity', label: 'Linha do tempo', icon: <TimelineIcon fontSize="small" /> },
  { id: 'sessions', label: 'Logins e sessões', icon: <VpnKeyIcon fontSize="small" /> },
  { id: 'blocked', label: 'Recusas e bloqueios', icon: <GppBadIcon fontSize="small" /> },
  { id: 'ip-traffic', label: 'Tráfego por IP', icon: <LanIcon fontSize="small" /> },
  { id: 'errors', label: 'Erros', icon: <BugReportIcon fontSize="small" /> },
  { id: 'entity-access', label: 'Acessos a um registro', icon: <TravelExploreIcon fontSize="small" /> },
];

class UsageArea extends Component {
  constructor(props) {
    super(props);
    this.isAdmGlobal = false;
    const { userSession } = this.props;
    if (userSession == null || userSession.user == null || userSession.user.userrole == null) Api.logoff();
    (userSession?.user?.userrole || []).forEach((item) => { if (item?.role?.roletype == 0) this.isAdmGlobal = true; });
    // Abas já visitadas ficam MONTADAS (escondidas com display:none): trocar de aba não perde a
    // consulta, os filtros nem a página. Montagem é preguiçosa: só carrega ao abrir a aba pela 1ª vez.
    // ip: filtro de investigação compartilhado pelas 4 abas do fato (Atividade, Recusas, Erros, Quem acessou).
    // Entra pela URL (?ip=) e vive aqui; trocar de aba ou seguir links entre abas não perde.
    this.state = { visited: { [this.currentTab()]: true }, ip: (new URLSearchParams(props.location?.search || '').get('ip') || '').trim() };
    this.onTab = this.onTab.bind(this);
    this.setIp = this.setIp.bind(this);
  }

  componentDidUpdate() {
    const tab = this.currentTab();
    if (!this.state.visited[tab]) this.setState((s) => ({ visited: { ...s.visited, [tab]: true } }));
  }

  currentTab() {
    const t = this.props.match?.params?.tab;
    return TABS.some((x) => x.id === t) ? t : 'dashboard';
  }

  setIp(ip) { const v = (ip || '').trim(); if (v !== this.state.ip) this.setState({ ip: v }); }

  onTab(e, id) {
    const lang = this.props.match.params.lang;
    this.props.history.push(`/${lang}/usage/${id}`);
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu, intl } = this.props;
    const lang = this.props.match.params.lang;
    if (!this.isAdmGlobal) { document.location.href = `/${lang}/unavailable`; return null; }
    const tab = this.currentTab();
    const visited = { ...this.state.visited, [tab]: true };
    const common = { lang, history: this.props.history, location: this.props.location, intl, ip: this.state.ip, onIpChange: this.setIp };
    // `active`: a visão só reage a querystring nova (links entre visões) quando é a aba visível.
    const pane = (id, View) => (visited[id] ? <div key={id} style={{ display: tab === id ? 'block' : 'none' }}><View {...common} active={tab === id} /></div> : null);

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        {/* minWidth 0: <main> é item flex; sem isso ele cresce até a largura da tabela mais larga e a
            página corta à direita em vez de a caixa (overflowX auto) de cada grid rolar. */}
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })} style={{ minWidth: 0 }}>
          <div className={classes.drawerHeader} />
          <Container component="main" maxWidth="xl" style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />
            <Card style={{ marginBottom: 12 }}>
              <CardContent className="background-title-search" style={{ paddingBottom: 0 }}>
                <Typography variant="h5">{intl.formatMessage({ id: 'usage.title' })}</Typography>
                <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>{intl.formatMessage({ id: 'usage.subtitle' })}</Typography>
                <Tabs value={tab} onChange={this.onTab} variant="scrollable" scrollButtons="auto" textColor="primary" indicatorColor="primary">
                  {TABS.map((t) => <Tab key={t.id} value={t.id} icon={t.icon} iconPosition="start" label={intl.formatMessage({ id: `usage.tab.${t.id}` })} style={{ minHeight: 48 }} />)}
                </Tabs>
              </CardContent>
            </Card>
            {/* usage-area: mexx-global-override.css dá min-width 0 a todo Grid item (item flex) daqui para dentro;
                sem isso o item cresce até a largura da tabela e a caixa overflowX: auto de cada grid nunca rola. */}
            <Box className="usage-area">
              {pane('dashboard', UsageDashboard)}
              {pane('screens', UsageScreens)}
              {pane('endpoints', UsageEndpoints)}
              {pane('user-activity', UsageUserActivity)}
              {pane('sessions', UsageSessions)}
              {pane('blocked', UsageBlocked)}
              {pane('ip-traffic', UsageIpTraffic)}
              {pane('errors', UsageErrors)}
              {pane('entity-access', UsageEntityAccess)}
            </Box>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

UsageArea.propTypes = { classes: PropTypes.object.isRequired };

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});
const mapDispatchToProps = (dispatch) => bindActionCreators(componentActions, dispatch);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(UsageArea)));
